import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ModalComponent from 'components/Common/Modal';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { store } from 'components/Store';
import useGetReportList from 'components/Reports/useGetReportList';

// A small, chart-specific version of AddUpdatesToReport.jsx's new/existing-report picker - that
// component's headline/summary/credits logic is all about picking content updates, none of which
// applies to adding a chart widget, so this duplicates just the report-picker part rather than
// reaching for that much bigger component.
const AddEngagementStatsToReportModal = ({ isOpen, setIsOpen, localState }) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const navigate = useNavigate();

  const [reportOption, setReportOption] = useState('NewReport');
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getReportList } = useGetReportList();

  const fetchReports = useRef(null);
  fetchReports.current = async (source) => {
    const results = await getReportList({ source });
    if (results) {
      setReports(results);
      setSelectedReport(results?.[0]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const source = new AbortController();
    fetchReports.current(source);
    return () => source.abort('Report list cancelled by the user');
  }, [isOpen]);

  const submit = () => {
    dispatch({ type: 'MODIFY_SECTION', parameter: 'engagementStatsToCreateReport', value: { localState } });
    setIsOpen(false);
    navigate({ pathname: reportOption === 'NewReport' ? '/reports/create' : `/reports/${selectedReport?.id}` });
  };

  return (
    <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen} onAfterClose={() => setReportOption('NewReport')}>
      <h3 className='p-4 border-bottom title-h4-bold mb-0'>Add this chart to a report</h3>
      <div className='p-4'>
        <div className='flex-centered gap-10 mb-4'>
          <button
            className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
              reportOption === 'NewReport' ? 'selected' : ''
            }`}
            onClick={() => setReportOption('NewReport')}
          >
            <span className='select-item d-inline-block-centered mr-2' />A new report
          </button>
          <button
            className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
              reportOption === 'ExistingReport' ? 'selected' : ''
            }`}
            onClick={() => setReportOption('ExistingReport')}
          >
            <span className='select-item d-inline-block-centered mr-2' />
            An existing report
          </button>
        </div>

        {reportOption === 'ExistingReport' && (
          <div className='mb-5'>
            <DropdownComponent
              mainItem={() => (
                <span className='specific-y-padding-5 d-inline-block' style={{ maxHeight: '47px' }}>
                  {isLoading ? (
                    <SkeletonTheme baseColor='#fff'>
                      <Skeleton height={15} width={'200px'} />
                    </SkeletonTheme>
                  ) : (
                    <span>{selectedReport ? selectedReport?.title : 'Select a report'}</span>
                  )}
                </span>
              )}
            >
              <CustomScrollbar className='simple-scrollbar' autoHeight autoHeightMin='100%' autoHeightMax='226px' maximalThumbYSize={100}>
                {isLoading ? (
                  <SkeletonTheme baseColor='#fff'>
                    {Array(4)
                      .fill()
                      .map((item, index) => (
                        <div className='px-3 py-2 w-100' key={index}>
                          <Skeleton height={22} width={'100%'} />
                        </div>
                      ))}
                  </SkeletonTheme>
                ) : reports?.length === 0 ? (
                  <div className='p-3 light-60-text font-italic'>You have not created any reports yet</div>
                ) : (
                  <ul className='policy-list px-0 py-1 mb-0'>
                    {reports?.map((item) => (
                      <li
                        onClick={() => setSelectedReport(item)}
                        className='flex-centered specific-y-padding-8 px-3 list-action-button pointer'
                        key={`report-${item.id}`}
                      >
                        <span className='flex-grow-1'>{item.title}</span>
                        {selectedReport?.id === item.id && <span className='icon-tick paragraph-p4' />}
                      </li>
                    ))}
                  </ul>
                )}
              </CustomScrollbar>
            </DropdownComponent>
          </div>
        )}

        <div className='d-flex justify-content-end pt-3'>
          <button
            onClick={submit}
            disabled={reportOption === 'ExistingReport' && !selectedReport}
            className='general-button rounded-pill action-button px-5 specific-y-padding-8'
          >
            {reportOption === 'NewReport' ? 'Create report' : 'Add to report'}
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default AddEngagementStatsToReportModal;
